import {
  Table, Text, Group, Title, Stack, Button, Avatar, Badge, Container, Loader, Center,
} from "@mantine/core";
import ResponsiveTable from "../components/ResponsiveTable";
import { notifications } from "@mantine/notifications";
import { IconArrowLeft, IconRestore } from "@tabler/icons-react";
import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { getDeletedCandidates, restoreCandidate } from "../api";
import { useStore } from "../store";
import { formatCandidateName } from "../_Shared/candidateName";

export default function DeletedCandidatesPage() {
  const { t } = useTranslation("Veft");
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);
  const [, setLocation] = useLocation();
  const nameFormat = useStore((s) => s.nameFormat);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const data = await getDeletedCandidates();
      setCandidates(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load deleted candidates", e);
    } finally {
      setLoading(false);
    }
  }

  async function handleRestore(id, name) {
    setRestoring(id);
    try {
      await restoreCandidate(id);
      setCandidates((prev) => prev.filter((c) => c.id !== id));
      notifications.show({ title: t("restore_success_title"), message: t("restore_success_msg", { name }), color: "green" });
    } catch (e) {
      notifications.show({ color: "red", title: t("error"), message: t("deleted_restore_failed") });
    } finally {
      setRestoring(null);
    }
  }

  function daysUntilPurge(deletedAt) {
    const deleted = new Date(deletedAt);
    const purgeDate = new Date(deleted.getTime() + 30 * 24 * 60 * 60 * 1000);
    const remaining = Math.ceil((purgeDate - Date.now()) / (24 * 60 * 60 * 1000));
    return remaining;
  }

  return (
    <Container size="md" py="md">
    <Stack gap="lg">
      <Group justify="space-between">
        <Group gap="sm">
          <Button variant="subtle" size="xs" leftSection={<IconArrowLeft size={14} />} onClick={() => setLocation("/candidates")}>
            {t("back")}
          </Button>
          <Title order={3}>{t("deleted_candidates_title")}</Title>
          <Text size="sm" c="dimmed">{t("deleted_candidates_count", { count: candidates.length })}</Text>
        </Group>
      </Group>

      <Text size="sm" c="dimmed">
        {t("deleted_candidates_retention")}
      </Text>

      {loading ? (
        <Center py="xl"><Loader /></Center>
      ) : candidates.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">{t("deleted_candidates_empty")}</Text>
      ) : (
        <ResponsiveTable minWidth={520} highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("name")}</Table.Th>
              <Table.Th>{t("deleted_col_deleted")}</Table.Th>
              <Table.Th>{t("deleted_col_purge")}</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {candidates.map((c) => {
              const days = daysUntilPurge(c.deletedAt);
              return (
                <Table.Tr key={c.id}>
                  <Table.Td>
                    <Group gap="sm">
                      <Avatar size="sm" radius="xl" color="gray">
                        {c.name?.split(" ").map((n) => n[0]).join("").slice(0, 2)}
                      </Avatar>
                      <div>
                        <Text size="sm" fw={500}>{formatCandidateName(c, nameFormat)}</Text>
                        <Text size="xs" c="dimmed">{c.title}</Text>
                      </div>
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{new Date(c.deletedAt).toLocaleDateString()}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge size="sm" color={days <= 7 ? "red" : "yellow"}>
                      {days > 0 ? t("deleted_purge_days", { count: days }) : t("deleted_purge_imminent")}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Button
                      size="xs"
                      variant="light"
                      leftSection={<IconRestore size={14} />}
                      loading={restoring === c.id}
                      onClick={() => handleRestore(c.id, c.name)}
                    >
                      {t("restore")}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </ResponsiveTable>
      )}
    </Stack>
    </Container>
  );
}
